import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send, Sun, EyeOff, Eye } from "lucide-react";

export default function Conversation() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState([]);
  const [aiBadge, setAiBadge] = useState(false); // true when starters came from Claude
  const [err, setErr] = useState("");
  const boxRef = useRef(null);
  const suggestedRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setData(data);
      setTimeout(() => boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" }), 60);
      // Auto-suggest on first visit when no messages yet
      if (!suggestedRef.current && (data.messages || []).length === 0 && data.other?.id) {
        suggestedRef.current = true;
        suggest(data.other.id);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try { await api.post("/messages", { conversation_id: id, text: t }); setText(""); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const suggest = async (otherId) => {
    const oid = otherId || data?.other?.id;
    if (!oid) return;
    setAiBusy(true);
    try {
      const { data: r } = await api.post("/ai/conversation-starter", { other_user_id: oid });
      setAi(r.starters || []);
      setAiBadge(Boolean(r.ai));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  if (!data) return <div className="text-navy/50">Loading…</div>;
  const other = data.other || {};
  const bd = data.blind_depth || {};
  const daysLeft = Math.max(0, 7 - (bd.day_of_seven || 1) + 1);

  return (
    <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
      <div className="lg:col-span-2 glass rounded-3xl p-6 flex flex-col h-[70vh]">
        <div className="flex items-center justify-between border-b border-navy/10 pb-4">
          <Link to={`/app/user/${other.id}`} className="flex items-center gap-3" data-testid="convo-header">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-peach to-rose flex items-center justify-center text-white font-display relative">
              {bd.photos_hidden ? (
                <EyeOff size={16} />
              ) : (
                (other.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")
              )}
            </div>
            <div>
              <div className="font-medium text-navy">{other.name}{other.age ? `, ${other.age}` : ""}</div>
              <div className="text-xs text-navy/60">{other.city}</div>
            </div>
          </Link>
          <Link to={`/app/moments/${id}`} data-testid="convo-moments" className="text-sm text-rose hover:underline flex items-center gap-1">
            <Sun size={14} /> Moments
          </Link>
        </div>

        {/* Blind Depth banner */}
        {bd.photos_hidden && (
          <div className="mt-4 rounded-2xl border border-navy/10 bg-white/70 px-4 py-3 flex items-center gap-3" data-testid="blind-depth-banner">
            <EyeOff size={16} className="text-navy shrink-0" />
            <div className="flex-1 text-sm text-navy/80">
              <span className="font-medium">Blind Depth · Day {bd.day_of_seven} of 7.</span> Photos reveal in
              <span className="font-mono ml-1">{daysLeft} day{daysLeft === 1 ? "" : "s"}</span>. Get to know their story first.
            </div>
          </div>
        )}
        {!bd.photos_hidden && (
          <div className="mt-4 rounded-2xl border border-ivy/30 bg-ivy/5 px-4 py-3 flex items-center gap-3" data-testid="blind-depth-revealed">
            <Eye size={16} className="text-ivy shrink-0" />
            <div className="flex-1 text-sm text-navy/80">
              <span className="font-medium">Depth reached.</span> Photos are now visible on their profile.
            </div>
          </div>
        )}

        <div ref={boxRef} className="flex-1 overflow-auto py-4 space-y-2" data-testid="convo-messages">
          {data.messages.length === 0 && (
            <div className="text-center text-navy/50 mt-10">
              <div className="font-display text-2xl text-navy">Say something meaningful.</div>
              <div className="text-sm mt-1">Try a prompt below, or ask Wholesome AI for an opener.</div>
            </div>
          )}
          {data.messages.map((m) => {
            const mine = m.from === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                  mine ? "bg-navy text-cream-100 rounded-br-sm" : "bg-white/80 text-navy border border-white/70 rounded-bl-sm"
                }`}>
                  {m.text}
                  {m.flags?.length > 0 && (
                    <div className="mt-1 text-[10px] text-rose-200 italic">Safety flag: {m.flags[0]}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="border-t border-navy/10 pt-4 flex items-end gap-3">
          <textarea data-testid="convo-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Write with kindness…"
            className="flex-1 px-4 py-3 rounded-2xl bg-white/80 border border-navy/10 resize-none" />
          <button data-testid="convo-send" type="submit" disabled={busy || !text.trim()}
            className="btn-pill bg-rose text-white hover:bg-rose-600 disabled:opacity-50 flex items-center gap-1">
            <Send size={16} /> Send
          </button>
        </form>
      </div>

      <aside className="space-y-4">
        {bd.today_prompt && (
          <div className="glass rounded-3xl p-6" data-testid="daily-prompt-card">
            <div className="text-xs uppercase tracking-widest text-rose">Today's prompt · Day {bd.day_of_seven}</div>
            <blockquote className="mt-3 font-display italic text-navy text-lg leading-relaxed" style={{ fontFamily: "Fraunces" }}>
              "{bd.today_prompt}"
            </blockquote>
            <button
              data-testid="use-daily-prompt"
              onClick={() => setText(bd.today_prompt)}
              className="mt-3 btn-pill text-xs bg-navy text-cream-100 hover:bg-navy-600 flex items-center gap-1"
            >
              <Sparkles size={12} /> Use this
            </button>
          </div>
        )}
        <div className="glass rounded-3xl p-6" data-testid="ai-openers-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-rose flex items-center gap-1.5">
                Wholesome AI
                {aiBadge && ai.length > 0 && (
                  <span data-testid="ai-badge" className="text-[9px] px-1.5 py-0.5 rounded-full bg-ivy/10 text-ivy tracking-widest">
                    CRAFTED FOR YOU
                  </span>
                )}
              </div>
              <h3 className="font-display text-xl text-navy mt-0.5">Conversation openers</h3>
            </div>
            <button data-testid="ai-suggest" onClick={() => suggest()} disabled={aiBusy}
              className="btn-pill text-xs bg-navy text-cream-100 hover:bg-navy-600 flex items-center gap-1">
              <Sparkles size={12} /> {aiBusy ? "Thinking…" : ai.length ? "Rewrite" : "Suggest"}
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {aiBusy && ai.length === 0 && (
              <div className="text-sm text-navy/50 italic py-3" data-testid="ai-thinking">
                Wholesome AI is reading their story…
              </div>
            )}
            {(ai.length ? ai : data.prompts || []).map((p, i) => (
              <button key={i} data-testid={`prompt-${i}`} onClick={() => setText(p)}
                className="w-full text-left text-sm px-4 py-3 rounded-2xl bg-white/70 border border-white/70 hover:bg-white transition-all duration-500">
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="glass rounded-3xl p-6">
          <div className="text-xs uppercase tracking-widest text-rose">Safety</div>
          <div className="text-sm text-navy/70 mt-2">Never send money, gift cards, or personal ID. Report anything that feels off.</div>
        </div>
      </aside>

      {err && <div className="text-sm text-rose-600" data-testid="convo-error">{err}</div>}
    </div>
  );
}
